/** packages/shared-components/src/utils/keyboardLayoutUtils.ts */

import { KeyConfig, KeyboardType } from "../components/Keyboard/types";
import {
  getKeyCodeInfo,
  type KeyCodeInfo,
  KEYBOARD_LAYOUTS,
  KEYCODE_TO_WEB_KEY_MAP,
  isEmptyKey,
  isPierceKey,
} from "./keyCodeMapper";
import { X75V2_KNOB_DEVICE_VALUE } from "./x75v2FactoryPosFromInitJson";
import { M75_KNOB_DEVICE_VALUE } from "./m75FactoryPosFromInitJson";
import {
  KEYCODE_MAP_BEIYING,
  resolveEs87LayoutCellDisplayValue,
  findBeiyingKeyCap,
} from "./keyCodeMapperBeiying";
import { KEYCODE_MAP_HFD } from "./keyCodeMapperHfd";
import { resolveHfdFnSyntheticKeyInfo } from "./hfdFnDisplay";
import {
  resolveZhwxConsumerUiKeyCode,
  resolveZhwxFnSyntheticKeyInfo,
} from "@eweadn-qiankun/shared-utils";
import { getGapRulesMap } from "../components/Keyboard/gapConfig";
import { getKeyCodeMatrix } from "../components/Keyboard/keyboardLayouts";
import { isX75v3KeyboardType } from "./sliceFirstRowKeyboardMatrix";
import {
  getRongyuanUiKeyLabel,
  isBeiyingDevice8815X98Keyboard,
  isRongyuanAdvancedUiKeyCode,
  isZhwxKeyboardType,
  RONGYUAN_UI_DISABLED,
  TOTAL_DK75_SINGLE_KEY_WIDTHS,
  TOTAL_DK87_KEY_WIDTHS,
} from "@eweadn-qiankun/shared-utils";
import { isGamma75FnEmptyPhysicalCell } from "./gamma75FnLayerMatrix";
import { applyDk63V2SlashRShiftKeyWidths } from "./dk63v2Device";
import {
  applyX87MacBaseLayerToSdkRows,
  applyX87MacBaseLayerToKeyConfigs,
} from "./x87MacBaseLayerMatrix";
import {
  applyHuafendaX87V2FnLayerToSdkRows,
  shouldApplyHuafendaX87V2FnLayer,
} from "./huafendaX87V2FnLayerMatrix";
import {
  applyHuafendaM98ScreenFnLayerToSdkRows,
  shouldApplyHuafendaM98ScreenFnLayer,
} from "./huafendaM98ScreenFnLayerMatrix";
import {
  applyHuafendaSmart875FnLayerToSdkRows,
  shouldApplyHuafendaSmart875FnLayer,
} from "./huafendaSmart875FnLayerMatrix";
import {
  applyHuafendaX98_1KFnLayerToSdkRows,
  shouldApplyHuafendaX98_1KFnLayer,
} from "./huafendaX98_1KFnLayerMatrix";
// import { sanitizeDeep80MaxLayoutKeycodeRow } from "./deep80maxLightingGrid";

/**
 * Gamma75 / X75V3 底行 col10：
 * - Win 模式：固件偶发上报 R-Gui(231)，物理键为 R-Alt(230)
 * - X75 Ultra(x75v3) Mac 模式：该键位应显示 Win（勿强制改成 Alt）
 */
function normalizeX75CompactLayoutKeycode(
  keyboardType: KeyboardType,
  rowIndex: number,
  matrixCol: number,
  keyCode: number,
  system: number = 0
): number {
  if (!isX75v3KeyboardType(keyboardType)) return keyCode;
  if (rowIndex !== 5 || matrixCol !== 10) return keyCode;

  // 仅 X75 Ultra：Mac 模式下右侧 Alt 位显示 Win
  if (keyboardType === "x75v3" && system === 1) {
    if (keyCode === 230 || keyCode === 231 || keyCode === 4194304) {
      return 227;
    }
    return keyCode;
  }

  if (keyCode === 231 || keyCode === 4194304) {
    return 230;
  }
  return keyCode;
}

/**
 * 机甲 getKeyLayout / getKeyboardKeys 回显常用「分位 value」，与 KEYBOARD_LAYOUTS.m80 格内 HID 不一；
 * getKeyCodeInfo 只认 HID，故虚拟键盘需先映射再取文案。
 */
const MECHA_FIRMWARE_TO_LAYOUT_HID: Readonly<Record<number, number>> = {
  65536: 224,
  131072: 225,
  262144: 226,
  524288: 227,
  1048576: 228,
  2097152: 229,
  4194304: 230,
  218103808: 61697,
};

/** 智慧无限协议热键位掩码（误当作 HID 时会显示成 A/E/M/F7） */
const ZHWX_HOTKEY_TO_UI_HID: Readonly<Record<number, number>> = {
  0x01: 224,
  0x02: 225,
  0x04: 226,
  0x08: 227,
  0x10: 228,
  0x20: 229,
  0x40: 230,
  0x80: 231,
};

function resolveKeyCodeInfoForVirtualKey(
  keyCode: number,
  keyboardType: KeyboardType
): KeyCodeInfo | null {
  const hfdFnInfo = resolveHfdFnSyntheticKeyInfo(keyCode);
  if (hfdFnInfo) return hfdFnInfo;

  const zhwxFnInfo = resolveZhwxFnSyntheticKeyInfo(keyCode);
  if (zhwxFnInfo) return zhwxFnInfo;

  const direct = getKeyCodeInfo(keyCode);
  if (direct) return direct;

  if (isZhwxKeyboardType(keyboardType)) {
    const consumerUi = resolveZhwxConsumerUiKeyCode(keyCode);
    if (consumerUi !== keyCode) {
      const fromConsumer = getKeyCodeInfo(consumerUi);
      if (fromConsumer) return fromConsumer;
    }
    const mapped = ZHWX_HOTKEY_TO_UI_HID[keyCode];
    if (mapped != null) {
      const fromHid = getKeyCodeInfo(mapped);
      if (fromHid) return fromHid;
    }
    if (keyCode === 61697) return getKeyCodeInfo(61697);
  }

  if (keyCode === RONGYUAN_UI_DISABLED) return null;

  const rongyuanLabel = getRongyuanUiKeyLabel(keyCode);
  if (rongyuanLabel) {
    return {
      name: rongyuanLabel,
      type: "系统",
      comm: "",
      hex16: "",
      typeof: "basic",
    };
  }

  if (
    keyboardType !== "m80" &&
    keyboardType !== "m71" &&
    keyboardType !== "t87" &&
    keyboardType !== "x75v2" &&
    keyboardType !== "m75" &&
    keyboardType !== "X98_1K"
  ) {
    return null;
  }
  if (keyboardType === "x75v2" && keyCode === X75V2_KNOB_DEVICE_VALUE) {
    const knob = getKeyCodeInfo(8418);
    if (knob) return knob;
  }
  if (keyboardType === "m75" && keyCode === M75_KNOB_DEVICE_VALUE) {
    const knob = getKeyCodeInfo(8418);
    if (knob) return knob;
  }
  const mapped = MECHA_FIRMWARE_TO_LAYOUT_HID[keyCode];
  if (mapped != null) {
    const fromHid = getKeyCodeInfo(mapped);
    if (fromHid) return fromHid;
  }
  const cap = KEYCODE_MAP_BEIYING.find(
    (k) => k.keycode === keyCode || k.xsKeycode === keyCode
  );
  if (cap?.legend) {
    return {
      name: cap.legend,
      type: "系统",
      comm: "",
      hex16: "",
      typeof: "basic",
    };
  }
  const extCap = findBeiyingKeyCap(keyCode);
  if (extCap && "legend" in extCap && typeof extCap.legend === "string") {
    const comm =
      "name" in extCap && typeof extCap.name === "string" ? extCap.name : "";
    return {
      name: extCap.legend,
      type: "系统",
      comm,
      hex16: "",
      typeof: "basic",
    };
  }
  return null;
}

export type ResolveLayoutKeyLabelOptions = {
  firmwareRow?: number;
  firmwareCol?: number;
  solutionProvider?: string;
};

/** 与子应用 convertLayoutRows / patchLayoutKeyAtPosition 一致的键帽文案 */
export function resolveLayoutKeyDisplayLabel(
  keyCode: number,
  keyboardType: KeyboardType = "87",
  options: ResolveLayoutKeyLabelOptions = {}
): string {
  if (isPierceKey(keyCode)) return "△";
  if (isEmptyKey(keyCode) || keyCode === RONGYUAN_UI_DISABLED) return "";

  const row = options.firmwareRow ?? 0;
  const col = options.firmwareCol ?? 0;

  let keyInfo = resolveKeyCodeInfoForVirtualKey(keyCode, keyboardType);
  keyInfo = applyM71VirtualKeyLegendOverride(
    keyboardType,
    row,
    col,
    keyCode,
    keyInfo
  );
  if (keyInfo?.name) return keyInfo.name;

  const provider = options.solutionProvider?.toLowerCase();
  if (provider === "huafenda") {
    const hfd = KEYCODE_MAP_HFD.find(
      (item) => Number(item.keycode) === keyCode
    );
    if (hfd?.name) return hfd.name;
  }

  return String(keyCode);
}

/** 出厂态下固件 value 与矩阵 HID 不一致时的别名（改键后 value 已变，不再覆盖） */
function m71DefaultLegendAlias(matrixCode: number, keyCode: number): boolean {
  if (keyCode === matrixCode) return true;
  if (matrixCode === 21252 && keyCode === 101) return true;
  if (matrixCode === 229 && (keyCode === 2097152 || keyCode === 100))
    return true;
  if (matrixCode === 228 && keyCode === 1048576) return true;
  return false;
}

/** M71：仅出厂/别名回显时修正 Nubs、双 App 等文案；改键后保持 mapMachineRawToUiKey 结果 */
function applyM71VirtualKeyLegendOverride(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number,
  keyCode: number,
  info: KeyCodeInfo | null
): KeyCodeInfo | null {
  if (keyboardType !== "m71" || !info) return info;
  const matrix = KEYBOARD_LAYOUTS.m71 as number[][];
  const d = matrix[rowIndex]?.[colIndex];
  if (typeof d !== "number" || d === 0) return info;
  if (!m71DefaultLegendAlias(d, keyCode)) return info;
  let name: string | null = null;
  if (rowIndex === 4 && colIndex === 11) name = "R-Shift";
  else if (rowIndex === 5 && colIndex === 12) name = "Menu";
  else if (rowIndex === 5 && colIndex === 13) name = "R-Ctrl";
  if (!name) return info;
  return { ...info, name };
}

/**
 * M71 自定义灯：以静态矩阵格为准得到 layoutKeycode，避免实机 value（如 Nubs/双 App）与格语义错位，
 * 导致下发/右键清除打到错误灯位。
 */
export function resolveBeiyingM71LightingLayoutKeycode(
  cell:
    | {
        keyCode?: number;
        originalRow?: number;
        originalCol?: number;
      }
    | null
    | undefined
): number | undefined {
  if (
    !cell ||
    typeof cell.keyCode !== "number" ||
    !Number.isFinite(cell.keyCode)
  ) {
    return undefined;
  }
  const r = cell.originalRow;
  const c = cell.originalCol;
  if (r == null || c == null) return cell.keyCode;
  const matrix = KEYBOARD_LAYOUTS.m71 as number[][];
  const slot = matrix[r]?.[c];
  if (typeof slot === "number" && slot !== 0 && Number.isFinite(slot)) {
    return slot;
  }
  return cell.keyCode;
}

/**
 * X98_1K(8815) 自定义灯：以 `KEYBOARD_LAYOUTS.X98_1K` 格内 HID 为准。
 * getKeyLayout 对 Ctrl/Shift 等常回显分位 `value`（65536 等），与灯光 pos 解析用的矩阵 HID（224 等）不一致，
 * 仅信 `cell.keyCode` 会导致右键清除、单键调色找不到 pos。
 */
export function resolveBeiyingX98LightingLayoutKeycode(
  cell:
    | {
        keyCode?: number;
        originalRow?: number;
        originalCol?: number;
      }
    | null
    | undefined
): number | undefined {
  if (!cell) return undefined;
  const r = cell.originalRow;
  const c = cell.originalCol;
  if (r != null && c != null) {
    const matrix = KEYBOARD_LAYOUTS.X98_1K as number[][];
    const slot = matrix[r]?.[c];
    if (typeof slot === "number" && slot !== 0 && Number.isFinite(slot)) {
      return slot;
    }
  }
  const kc = cell.keyCode;
  if (typeof kc === "number" && Number.isFinite(kc)) return kc;
  return undefined;
}

/**
 * 贝盈 web-hid：标准 98（X98 8K）/ 99（ES98）矩阵格 HID。
 * 勿与 X98_1K(8815) 专用矩阵混用。
 */
export function resolveBeiyingStandardMatrixLightingLayoutKeycode(
  cell:
    | {
        keyCode?: number;
        originalRow?: number;
        originalCol?: number;
      }
    | null
    | undefined,
  matrixKey: "98" | "99" | "t87" | "m80"
): number | undefined {
  if (!cell) return undefined;
  const r = cell.originalRow;
  const c = cell.originalCol;
  if (r != null && c != null) {
    const matrix = KEYBOARD_LAYOUTS[matrixKey] as number[][] | undefined;
    if (matrix?.length) {
      const slot = matrix[r]?.[c];
      if (typeof slot === "number" && slot !== 0 && Number.isFinite(slot)) {
        return slot;
      }
    }
  }
  const kc = cell.keyCode;
  if (typeof kc === "number" && Number.isFinite(kc)) return kc;
  return undefined;
}

function readLocalEquipmentForBeiyingLighting(): {
  vendorId?: number;
  productId?: number;
} {
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("equipmentData")
        : null;
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * 自定义灯：按 keyboardType + VID/PID 选用 X98_1K(8815) 矩阵或标准 98/99 矩阵（与机甲 M71 分支一致）。
 */
export function resolveBeiyingLightingLayoutKeycodeForDevice(
  cell:
    | {
        keyCode?: number;
        originalRow?: number;
        originalCol?: number;
      }
    | null
    | undefined,
  keyboardType?: string
): number | undefined {
  const kt = String(keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (kt === "m71") return resolveBeiyingM71LightingLayoutKeycode(cell);
  if (kt === "m80" || kt === "t87" || kt === "x75v2" || kt === "m75") {
    return resolveBeiyingStandardMatrixLightingLayoutKeycode(
      cell,
      kt as "m80" | "t87"
    );
  }
  if (kt === "99") {
    return resolveBeiyingStandardMatrixLightingLayoutKeycode(cell, "99");
  }
  if (kt === "98" || kt === "x98_1k") {
    const eq = readLocalEquipmentForBeiyingLighting();
    if (isBeiyingDevice8815X98Keyboard(eq)) {
      return resolveBeiyingX98LightingLayoutKeycode(cell);
    }
    return resolveBeiyingStandardMatrixLightingLayoutKeycode(cell, "98");
  }
  const kc = cell?.keyCode;
  if (typeof kc === "number" && Number.isFinite(kc)) return kc;
  return undefined;
}

/** SDK 返回的行布局数据 */
export interface SDKRowLayout {
  layer: number;
  row: number;
  keyboardLayout: number[];
}

// 创建 KeyConfig 的参数对象
interface CreateKeyConfigParams {
  keyCode: number;
  multipleWidth: number;
  code: string;
  label: string;
  comm: string;
  originalRow: number;
  originalCol: number;
  uuid: string;
}

/** 静态矩阵格内 HID（与 SDK 层数据无关） */
function getStaticMatrixKeyCode(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number
): number {
  return getKeyCodeMatrix(keyboardType)?.[rowIndex]?.[colIndex] ?? 0;
}

/** 容园高级键槽仍显示物理键帽字母（MT 等模式由角标展示） */
function resolveRongyuanAdvancedSlotKeyInfo(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number,
  displayKeyCode: number
): KeyCodeInfo | null {
  if (keyboardType !== "rongyuan87") return null;
  if (!isRongyuanAdvancedUiKeyCode(displayKeyCode)) return null;
  const staticCode = getStaticMatrixKeyCode(keyboardType, rowIndex, colIndex);
  if (!staticCode) return null;
  return getKeyCodeInfo(staticCode);
}

/** M98 / M98TFT / M98 8K：小键盘 Enter 正下方是小键盘 0，不可 2U 竖跨 */
function isM98NumpadPad0LayoutFamily(keyboardType: KeyboardType): boolean {
  return (
    keyboardType === "M98" ||
    keyboardType === "M98TFT" ||
    keyboardType === "M98 8K"
  );
}

function isM98NumpadEnterAbovePad0(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number
): boolean {
  if (!isM98NumpadPad0LayoutFamily(keyboardType)) return false;
  return getStaticMatrixKeyCode(keyboardType, rowIndex + 1, colIndex) === 98;
}

/** 小键盘 + (87) / Enter (88) 为 2U 竖键的上半格 */
function isVerticalNumpadUpperCell(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number,
  keyCode?: number
): boolean {
  if (keyCode === 87 || keyCode === 88) {
    return !isM98NumpadEnterAbovePad0(keyboardType, rowIndex, colIndex);
  }
  const factory = getStaticMatrixKeyCode(keyboardType, rowIndex, colIndex);
  if (factory !== 87 && factory !== 88) return false;
  return !isM98NumpadEnterAbovePad0(keyboardType, rowIndex, colIndex);
}

/** 2U 竖键占据的下一行同列格：只保留列宽占位，不渲染键帽 */
function isVerticalNumpadLowerSpannedCell(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number
): boolean {
  if (colIndex < 0 || rowIndex <= 0) return false;
  const upper = getStaticMatrixKeyCode(keyboardType, rowIndex - 1, colIndex);
  if (upper !== 87 && upper !== 88) return false;
  if (
    isM98NumpadPad0LayoutFamily(keyboardType) &&
    getStaticMatrixKeyCode(keyboardType, rowIndex, colIndex) === 98
  ) {
    return false;
  }
  return true;
}

function resolveVerticalNumpadSpan(
  keyboardType: KeyboardType,
  rowIndex: number,
  colIndex: number,
  keyCode?: number
): number {
  return isVerticalNumpadUpperCell(keyboardType, rowIndex, colIndex, keyCode)
    ? 2
    : 1;
}

/**
 * 根据键盘类型获取默认宽度配置
 */
function readEquipmentVidPidForWidths(): {
  vendorId?: number;
  productId?: number;
  identifier?: string;
} {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return {};
    const eq = JSON.parse(raw) as {
      vendorId?: number;
      productId?: number;
      identifier?: string;
      displayname?: string;
      productName?: string;
      keyboardType?: string;
      solutionRovider?: string;
    };
    return {
      vendorId: eq?.vendorId,
      productId: eq?.productId,
      identifier:
        eq?.identifier ??
        `${eq?.displayname ?? ""} ${eq?.productName ?? ""} ${eq?.keyboardType ?? ""} ${eq?.solutionRovider ?? ""}`,
    };
  } catch {
    return {};
  }
}

export const getKeyWidthsByType = (type: KeyboardType): number[][] => {
  switch (type) {
    case "104":
    case "keymap104":
      return TOTAL_104_KEY_WIDTHS;
    case "DK108":
      return TOTAL_DK108_KEY_WIDTHS;
    case "87":
      return TOTAL_87_KEY_WIDTHS;
    case "es87":
      return TOTAL_ES87_KEY_WIDTHS;
    case "huafenda87":
    case "rongyuan87":
      return TOTAL_HUAFENDA87_KEY_WIDTHS;
    case "84":
      return TOTAL_84_KEY_WIDTHS;
    case "deep80max":
      return TOTAL_DEEP80MAX_KEY_WIDTHS;
    case "99":
      return TOTAL_99_KEY_WIDTHS;
    case "98":
    case "huafenda98":
      return TOTAL_98_KEY_WIDTHS;
    case "v102":
      return TOTAL_V102_KEY_WIDTHS;
    case "X98_1K":
      return TOTAL_98_KEY_WIDTHS;
    case "m80":
      return TOTAL_M80_KEY_WIDTHS;
    case "m71":
      return TOTAL_M71_KEY_WIDTHS;
    case "t87":
      return TOTAL_T87_KEY_WIDTHS;
    case "x75v2":
      return TOTAL_X75V2_KEY_WIDTHS;
    case "m75":
      return TOTAL_M75_KEY_WIDTHS;
    case "x75gc":
      return TOTAL_X75GC_KEY_WIDTHS;
    case "68":
      return TOTAL_68_KEY_WIDTHS;
    case "huafenda68":
      return TOTAL_HUAFENDA68_KEY_WIDTHS;
    case "tank68he":
      return TOTAL_TANK68HE_KEY_WIDTHS;
    case "68EVO":
      return TOTAL_68EVO_KEY_WIDTHS;
    case "63": {
      // 公版 DK63 保持 /?=1、R-Shift=1.9；DK63 V2 / 华奋达 DK63Star 互换为 1.9 / 1
      const { vendorId, productId, identifier } =
        readEquipmentVidPidForWidths();
      return (
        applyDk63V2SlashRShiftKeyWidths(
          TOTAL_63_KEY_WIDTHS,
          productId,
          vendorId,
          identifier
        ) ?? TOTAL_63_KEY_WIDTHS
      );
    }
    case "dk63Machine":
      return TOTAL_DK63_MACHINE_KEY_WIDTHS;
    case "75Pro":
      return TOTAL_75PRO_KEY_WIDTHS;
    case "x75v3":
      return TOTAL_X75V3_KEY_WIDTHS;
    case "gamma75":
      return TOTAL_GAMMA75_KEY_WIDTHS;
    case "dk75":
    case "dk75Pro":
    case "dk75E":
      return TOTAL_DK75_KEY_WIDTHS;
    case "dk87":
      return TOTAL_DK87_KEY_WIDTHS;
    case "dk75Single":
      return TOTAL_DK75_SINGLE_KEY_WIDTHS;
    case "M98":
      return TOTAL_M98_KEY_WIDTHS;
    case "75ProTFT":
      return TOTAL_75PROTFT_KEY_WIDTHS;
    case "M98TFT":
      return TOTAL_M98TFT_KEY_WIDTHS;
    case "M98 8K":
      return TOTAL_M98_8K_KEY_WIDTHS;
    case "special":
      return [[1, 1, 1]];
    default:
      return TOTAL_104_KEY_WIDTHS;
  }
};

/** 虚拟键盘 / 改键 publish 统一行数（与 getKeyWidthsByType 矩阵一致） */
export function getKeyboardLayoutRowCount(
  keyboardType: KeyboardType | string
): number {
  return getKeyWidthsByType(String(keyboardType) as KeyboardType).length;
}

/** 远程同步 layout 是否包含完整行数，防止不完整 rows 覆盖虚拟键盘 */
export function isRemoteLayoutRowsComplete(
  rows: unknown[] | undefined | null,
  keyboardType: KeyboardType | string
): boolean {
  const expected = getKeyboardLayoutRowCount(keyboardType);
  return Array.isArray(rows) && rows.length >= expected;
}

/**
 * 获取某行某列后是否需要插入间距
 */
const getGapAfterKey = (
  rowIndex: number,
  colIndex: number,
  keyboardType: KeyboardType
): number => {
  const gapMap = getGapRulesMap(keyboardType);
  if (keyboardType === "m80") return 0;
  return gapMap[rowIndex]?.[colIndex] || 0;
};

/**
 * ES87 Shift 行：固件/SDK 常为 [LShift,Z…,RShift,0,0,↑]（无 LShift 后占位列），
 * 展示需与 LAYOUT_87 同构为 [LShift,0,Z…,RShift,0,0,↑] 才能与 TOTAL_ES87 宽度列对齐。
 */
function normalizeEs87DisplayKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (keyboardType !== "es87" || rowIndex !== 4) return keyCodes;
  if (keyCodes[1] === 0) return keyCodes;
  if (keyCodes[0] === 225 && keyCodes[1] !== 0) {
    return [keyCodes[0], 0, ...keyCodes.slice(1)];
  }
  return keyCodes;
}

/**
 * Gamma75 Shift 行：固件/SDK 常为 [LShift,Z,…]（无 LShift 后占位列），
 * 与 ES68 相同须规范为 [LShift,0,Z,…]，否则 Z 起整行轴体/性能列号错位。
 */
function normalizeGamma75ShiftRowKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (
    keyboardType !== "gamma75" &&
    keyboardType !== "dk75" &&
    keyboardType !== "dk75Pro" &&
    keyboardType !== "dk75E"
  ) {
    return keyCodes;
  }
  if (rowIndex !== 4) return keyCodes;
  if (keyCodes[1] === 0) return keyCodes;
  if (keyCodes[0] === 225 && keyCodes[1] !== 0) {
    return [keyCodes[0], 0, ...keyCodes.slice(1)];
  }
  return keyCodes;
}

/**
 * DEEP80 MAX / SMART875(84) Shift 行：SDK 紧凑列为 [LShift,Z,X,…]，
 * UI 矩阵为 [LShift,0,Z,X,…]；未插入占位列时会在 LShift 后多出一颗 Z。
 */
function normalizeDeep80MaxDisplayKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (rowIndex !== 4) return keyCodes;
  if (keyboardType !== "deep80max" && keyboardType !== "84") return keyCodes;
  if (keyCodes[1] === 0) return keyCodes;
  if (keyCodes[0] === 225 && keyCodes[1] !== 0) {
    return [keyCodes[0], 0, ...keyCodes.slice(1)];
  }
  return keyCodes;
}

const USES_68_SHIFT_ROW_GAP_PLACEHOLDER = new Set([
  "68",
  "huafenda68",
  "tank68he",
]);

/**
 * 68 / BAT68 / Tank68 HE Shift 行：固件常为 [LShift,Z,…]（无占位列），
 * 展示矩阵为 [LShift,0,Z,…]；否则 Z 落在 width=0 的 col1 上整颗键消失、整行错位。
 */
/** DK75 Single：Shift 行 col1 为矩阵占位列，展示矩阵须置 0 避免 Z 键消失 */
function normalizeDk75SingleDisplayKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (keyboardType !== "dk75Single") return keyCodes;
  if (rowIndex === 4) {
    const next = keyCodes.slice();
    next[1] = 0;
    return next;
  }
  return keyCodes;
}

/** DK87 ANSI：固件矩阵 (3,12) K29、(4,1) K45 无实体键帽 */
function normalizeDk87DisplayKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (keyboardType !== "dk87") return keyCodes;
  if (rowIndex === 3) {
    const next = keyCodes.slice();
    next[12] = 0;
    return next;
  }
  if (rowIndex === 4) {
    const next = keyCodes.slice();
    next[1] = 0;
    return next;
  }
  return keyCodes;
}

function normalize68ShiftRowGapKeyCodes(
  keyCodes: number[],
  rowIndex: number,
  keyboardType: KeyboardType
): number[] {
  if (rowIndex !== 4) return keyCodes;
  if (!USES_68_SHIFT_ROW_GAP_PLACEHOLDER.has(String(keyboardType))) {
    return keyCodes;
  }
  if (keyCodes[1] === 0) return keyCodes;
  if (keyCodes[0] === 225 && keyCodes[1] !== 0) {
    return [keyCodes[0], 0, ...keyCodes.slice(1)];
  }
  return keyCodes;
}

/** 展示矩阵插入占位列后，originalCol 仍映射回 SDK/固件列 */
function resolveEs87OriginalCol(
  rowIndex: number,
  index: number,
  keyCode: number,
  keyboardType: KeyboardType,
  shiftRowExpanded: boolean
): number {
  if (keyboardType !== "es87" || rowIndex !== 4 || !shiftRowExpanded) {
    return index;
  }
  if (index === 1 && keyCode === 0) return -1;
  if (index >= 2) return index - 1;
  return index;
}

function padKeyCodesToWidthRow(
  keyCodes: number[],
  defaultWidths: number[]
): number[] {
  if (keyCodes.length >= defaultWidths.length) return keyCodes;
  return [
    ...keyCodes,
    ...Array(defaultWidths.length - keyCodes.length).fill(0),
  ];
}

/**
 * DK63 / DK63 V2：固件 getKeyLayout 常为紧凑列，须展开为与 TOTAL_63_KEY_WIDTHS 一致的 14 列再展示。
 * 占位列（width=0）上的非 0 键码须清零，否则会渲染成假键帽；col11–13 键码不重排，与改键列号一致。
 */
export function normalizeDk63SdkKeycodeRow(
  keyCodes: number[],
  rowIndex: number,
  defaultWidths: number[]
): number[] {
  const targetLen = defaultWidths.length;
  let row = padKeyCodesToWidthRow(keyCodes, defaultWidths).slice(0, targetLen);

  if (rowIndex === 3 && row[12] === 40 && row[11] === 52) {
    row = [...row.slice(0, 12), 0, 40];
  }

  if (
    rowIndex === 4 &&
    row[0] === 225 &&
    row[1] === 29 &&
    (defaultWidths[1] ?? 0) === 0
  ) {
    row = [row[0], 0, ...row.slice(1)].slice(0, targetLen);
  }

  if (rowIndex === 5) {
    const spaceCol = defaultWidths.findIndex((w) => w > 4);
    if (spaceCol >= 0 && row[spaceCol] !== 44) {
      const from = row.findIndex((c) => c === 44);
      if (from >= 0 && from !== spaceCol) {
        row[from] = 0;
        row[spaceCol] = 44;
      }
    }
  }

  if (rowIndex === 3 && (defaultWidths[12] ?? 0) === 0 && row[12] === 46) {
    row[12] = 0;
  }

  return row.map((code, i) =>
    (defaultWidths[i] ?? 0) === 0 && code !== 0 ? 0 : code
  );
}

/**
 * M80 机甲：afterIndex 按“在该索引按键前插入间距”解释
 */
const getGapBeforeKey = (
  rowIndex: number,
  colIndex: number,
  keyboardType: KeyboardType
): number => {
  if (keyboardType !== "m80") return 0;
  const gapMap = getGapRulesMap(keyboardType);
  return gapMap[rowIndex]?.[colIndex] || 0;
};

/**
 * 将 SDK KeyCode 数组转换为 KeyConfig 数组
 * ✅ 自动从 KEYCODE_MAP 获取 label
 * ✅ 支持在指定位置动态插入间距
 * ✅ 保存原始 keyCode 和位置信息（originalRow, originalCol）
 */
export const convertRowLayoutToKeyConfigs = (
  keyCodes: number[],
  defaultWidths: number[],
  rowIndex: number,
  keyboardType: KeyboardType = "87",
  firmwareCols?: number[],
  layer: number = 0,
  system: number = 0
): KeyConfig[] => {
  const result: KeyConfig[] = [];
  const rawKeyCodes = keyCodes;
  const expandedShiftRow =
    keyboardType === "es87" &&
    rowIndex === 4 &&
    rawKeyCodes[0] === 225 &&
    rawKeyCodes[1] !== 0;
  let layoutKeyCodes = normalizeEs87DisplayKeyCodes(
    keyCodes,
    rowIndex,
    keyboardType
  );
  layoutKeyCodes = normalizeDeep80MaxDisplayKeyCodes(
    layoutKeyCodes,
    rowIndex,
    keyboardType
  );
  layoutKeyCodes = normalize68ShiftRowGapKeyCodes(
    layoutKeyCodes,
    rowIndex,
    keyboardType
  );
  layoutKeyCodes = normalizeDk75SingleDisplayKeyCodes(
    layoutKeyCodes,
    rowIndex,
    keyboardType
  );
  layoutKeyCodes = normalizeDk87DisplayKeyCodes(
    layoutKeyCodes,
    rowIndex,
    keyboardType
  );
  layoutKeyCodes = padKeyCodesToWidthRow(layoutKeyCodes, defaultWidths);
  if (keyboardType === "63") {
    layoutKeyCodes = normalizeDk63SdkKeycodeRow(
      layoutKeyCodes,
      rowIndex,
      defaultWidths
    );
  }

  // 创建 KeyConfig 的工具函数
  const createKeyConfig = ({
    keyCode,
    multipleWidth,
    code,
    label,
    comm,
    originalRow,
    originalCol,
    uuid,
  }: CreateKeyConfigParams): KeyConfig => {
    const verticalSpan = resolveVerticalNumpadSpan(
      keyboardType,
      originalRow,
      originalCol,
      keyCode
    );
    return {
      code,
      label,
      comm,
      keyCode,
      multipleWidth,
      originalRow,
      originalCol,
      uuid,
      ...(verticalSpan > 1 ? { verticalSpan } : {}),
    };
  };

  layoutKeyCodes?.forEach((keyCode, index) => {
    // DEEP80 MAX / 84：Shift 行 col1 为矩阵占位列，永不渲染键帽
    if (
      rowIndex === 4 &&
      index === 1 &&
      (keyboardType === "deep80max" || keyboardType === "84")
    ) {
      return;
    }

    let displayKeyCode = keyCode;
    if (keyboardType === "es87") {
      const defaultHid = (KEYBOARD_LAYOUTS.es87 as number[][])?.[rowIndex]?.[
        index
      ];
      if (defaultHid === 104) {
        displayKeyCode = resolveEs87LayoutCellDisplayValue(defaultHid, keyCode);
      }
    }
    const matrixColForNormalization =
      isX75v3KeyboardType(keyboardType) && firmwareCols?.[index] !== undefined
        ? firmwareCols[index]
        : index;
    displayKeyCode = normalizeX75CompactLayoutKeycode(
      keyboardType,
      rowIndex,
      matrixColForNormalization,
      displayKeyCode,
      system
    );
    const originalCol = resolveEs87OriginalCol(
      rowIndex,
      index,
      displayKeyCode,
      keyboardType,
      expandedShiftRow
    );
    const gapBeforeWidth = getGapBeforeKey(rowIndex, index, keyboardType);
    if (gapBeforeWidth > 0) {
      result.push(
        createKeyConfig({
          keyCode: 0,
          multipleWidth: gapBeforeWidth,
          code: "__gap__",
          label: "",
          comm: "",
          originalRow: rowIndex,
          originalCol: -1,
          uuid: `__gap_before__-${rowIndex}-${index}`,
        })
      );
    }

    const keyInfoRaw = resolveKeyCodeInfoForVirtualKey(
      displayKeyCode,
      keyboardType
    );
    let keyInfo = applyM71VirtualKeyLegendOverride(
      keyboardType,
      rowIndex,
      index,
      displayKeyCode,
      keyInfoRaw
    );
    const rongyuanPhysicalInfo = resolveRongyuanAdvancedSlotKeyInfo(
      keyboardType,
      rowIndex,
      originalCol,
      displayKeyCode
    );
    if (rongyuanPhysicalInfo) {
      keyInfo = rongyuanPhysicalInfo;
    }
    const uuidSourceCode = rongyuanPhysicalInfo
      ? getStaticMatrixKeyCode(keyboardType, rowIndex, originalCol)
      : displayKeyCode;
    const uuid = `${keyInfo?.name}-${uuidSourceCode}-${keyInfo?.hex16}-${rowIndex}-${index}`;

    const matrixColForCell =
      isX75v3KeyboardType(keyboardType) && firmwareCols?.[index] !== undefined
        ? firmwareCols[index]
        : index;

    if (
      isVerticalNumpadLowerSpannedCell(keyboardType, rowIndex, matrixColForCell)
    ) {
      const spanWidth = defaultWidths[index] ?? 0;
      if (spanWidth > 0) {
        result.push(
          createKeyConfig({
            keyCode: 0,
            multipleWidth: spanWidth,
            code: "__gap__",
            label: "",
            comm: "",
            originalRow: rowIndex,
            originalCol: matrixColForCell,
            uuid: `__numpad_span__-${rowIndex}-${index}`,
          })
        );
      }
      const gapWidth = getGapAfterKey(rowIndex, index, keyboardType);
      if (gapWidth > 0) {
        result.push(
          createKeyConfig({
            keyCode: 0,
            multipleWidth: gapWidth,
            code: "__gap__",
            label: "",
            comm: "",
            originalRow: rowIndex,
            originalCol: -1,
            uuid: `__gap__-${rowIndex}-${index}`,
          })
        );
      }
      return;
    }

    const isGamma75FnBlankKey =
      keyboardType === "gamma75" &&
      layer !== 0 &&
      isGamma75FnEmptyPhysicalCell(rowIndex, matrixColForCell, displayKeyCode);
    /** V80(dakai) Fn 层空键：Base 有实键但 SDK 报 0，须保留可见空键帽 */
    const is87FnBlankKey =
      keyboardType === "87" &&
      layer !== 0 &&
      displayKeyCode === 0 &&
      (getKeyCodeMatrix("87")[rowIndex]?.[matrixColForCell] ?? 0) !== 0;
    const isX98_1KFnEmptyKey =
      keyboardType === "X98_1K" && layer !== 0 && isPierceKey(displayKeyCode);
    if (isGamma75FnBlankKey || is87FnBlankKey || isX98_1KFnEmptyKey) {
      const rowTable = getKeyWidthsByType(keyboardType)[rowIndex] ?? [];
      const blankWidth =
        rowTable[matrixColForCell] ?? defaultWidths[index] ?? 1;
      /** 小键盘 + / Enter 为 2U 竖键，Fn 空键帽须保留静态矩阵 keyCode 以撑高 */
      const staticMatrixCode = getStaticMatrixKeyCode(
        keyboardType,
        rowIndex,
        matrixColForCell
      );
      const preserveVerticalNumpadHeight =
        isX98_1KFnEmptyKey &&
        (staticMatrixCode === 87 || staticMatrixCode === 88);
      result.push(
        createKeyConfig({
          keyCode: preserveVerticalNumpadHeight ? staticMatrixCode : 0,
          multipleWidth: blankWidth,
          code: "KEY_0",
          label: "",
          comm: keyInfo?.comm ?? "",
          originalRow: rowIndex,
          originalCol: matrixColForCell,
          uuid,
        })
      );
      const gapWidth = getGapAfterKey(rowIndex, index, keyboardType);
      if (gapWidth > 0) {
        result.push(
          createKeyConfig({
            keyCode: 0,
            multipleWidth: gapWidth,
            code: "__gap__",
            label: "",
            comm: "",
            originalRow: rowIndex,
            originalCol: -1,
            uuid: `__gap__-${rowIndex}-${index}`,
          })
        );
      }
      return;
    }

    // 处理空键（SDK 矩阵占位）
    if (isEmptyKey(displayKeyCode)) {
      // 仅 68 系等左 Shift 与 Z 之间可能占位 0；M71/M80/X75V2/M75 机甲 (4,1) 为 Z，误滤会吞掉整颗键
      if (
        rowIndex === 4 &&
        index === 1 &&
        keyboardType !== "m71" &&
        keyboardType !== "m80" &&
        keyboardType !== "x75v2" &&
        keyboardType !== "x75gc" &&
        keyboardType !== "m75" &&
        keyboardType !== "dk63Machine"
      ) {
        return;
      }
      const emptyWidth =
        isX75v3KeyboardType(keyboardType) && rowIndex !== 5
          ? 0
          : defaultWidths[index] || 0;
      const gapOriginalCol = isX75v3KeyboardType(keyboardType)
        ? (firmwareCols?.[index] ?? index)
        : originalCol;
      result.push(
        createKeyConfig({
          keyCode: displayKeyCode,
          multipleWidth: emptyWidth,
          code: "__gap__",
          label: "",
          comm: keyInfo?.comm || "",
          originalRow: rowIndex,
          originalCol: gapOriginalCol,
          uuid,
        })
      );
    } else {
      const webKeySource =
        keyboardType === "m80" ||
        keyboardType === "m71" ||
        keyboardType === "t87" ||
        keyboardType === "x75v2" ||
        keyboardType === "x75gc" ||
        keyboardType === "m75" ||
        keyboardType === "X98_1K"
          ? (MECHA_FIRMWARE_TO_LAYOUT_HID[displayKeyCode] ?? displayKeyCode)
          : displayKeyCode;
      const webKey =
        KEYCODE_TO_WEB_KEY_MAP[webKeySource] || `KEY_${displayKeyCode}`;

      if (displayKeyCode === RONGYUAN_UI_DISABLED) {
        result.push(
          createKeyConfig({
            keyCode: displayKeyCode,
            multipleWidth: defaultWidths[index] || 1,
            code: "__NOT_EXIST_",
            label: "",
            comm: "",
            originalRow: rowIndex,
            originalCol,
            uuid,
          })
        );
      } else if (!keyInfo) {
        const fallbackLabel = getRongyuanUiKeyLabel(displayKeyCode);
        if (!fallbackLabel) {
          console.warn(`Unknown keyCode: ${displayKeyCode} at index ${index}`);
        }
        const code = displayKeyCode === 0 ? "__gap__" : "__NOT_EXIST_";
        const width = displayKeyCode === 0 ? 0 : defaultWidths[index] || 1;
        result.push(
          createKeyConfig({
            keyCode: displayKeyCode,
            multipleWidth: width,
            code,
            label: fallbackLabel,
            comm: "",
            originalRow: rowIndex,
            originalCol,
            uuid,
          })
        );
      } else {
        // 处理正常按键或穿透键
        const isPierced = isPierceKey(displayKeyCode);
        /** Fn 层穿透键在改键页须可点：x75v3 / gamma75 / es87；特殊键面板（gamma75）亦须展示穿透 */
        const isFnLayerPierceVisible =
          isPierced &&
          ((layer !== 0 &&
            (isX75v3KeyboardType(keyboardType) || keyboardType === "es87")) ||
            keyboardType === "special");
        const matrixCol =
          isX75v3KeyboardType(keyboardType) &&
          firmwareCols?.[index] !== undefined
            ? firmwareCols[index]
            : isX75v3KeyboardType(keyboardType)
              ? resolveX75v3FirmwareMatrixCol(
                  keyboardType,
                  rowIndex,
                  keyCode,
                  index
                )
              : originalCol;
        result.push(
          createKeyConfig({
            keyCode: displayKeyCode,
            multipleWidth: isFnLayerPierceVisible
              ? defaultWidths[index] || 1
              : defaultWidths[index] || (isPierced ? 0 : 1), // 穿透键宽度设为 0
            code: webKey,
            /** 特殊键面板穿透键显示 △（与 KEYCODE_MAP / Fn 层一致） */
            label: keyInfo.name,
            comm: keyInfo.comm,
            originalRow: rowIndex,
            originalCol: matrixCol,
            uuid,
          })
        );
      }
    }

    // 动态插入间距
    const gapWidth = getGapAfterKey(rowIndex, index, keyboardType);
    if (gapWidth > 0) {
      if (
        rowIndex === 4 &&
        index === 1 &&
        keyboardType !== "m71" &&
        keyboardType !== "m80" &&
        keyboardType !== "x75v2" &&
        keyboardType !== "x75gc" &&
        keyboardType !== "m75" &&
        keyboardType !== "dk63Machine"
      ) {
        return;
      }
      result.push(
        createKeyConfig({
          keyCode: 0,
          multipleWidth: gapWidth,
          code: "__gap__",
          label: "",
          comm: keyInfo?.comm || "",
          originalRow: rowIndex,
          originalCol: -1,
          uuid,
        })
      );
    }
  });
  return result;
};

/** 批量转换多行布局 */
const DK75_STRIP_FROW_KEYCODES = new Set([70, 71, 72, 73]);

/** DK75 系列：SDK 矩阵对齐静态布局（Caps 行 `'` 后插占位列；底行去 RCtrl） */
function normalizeDk75FamilySdkKeycodeRow(
  rowIndex: number,
  sdkRow: number[],
  keyboardType: "dk75" | "dk75Pro" | "dk75E"
): number[] {
  const template = getKeyCodeMatrix(keyboardType)[rowIndex] ?? [];
  const out = [...template];
  const sdk = trimKeyCodeRowTrailingZeros(sdkRow);

  if (rowIndex === 0) {
    // DK75 E：F 行 PrtScr + Insert 均为实键
    if (keyboardType === "dk75E") {
      for (let i = 0; i < out.length && i < sdk.length; i++) {
        if (sdk[i] !== 0) {
          out[i] = sdk[i];
        }
      }
      return out;
    }
    // DK75 标准款：PrtScr 实键可改键，col14 旋钮占位
    if (keyboardType === "dk75") {
      for (let i = 0; i < out.length && i < sdk.length; i++) {
        if (sdk[i] !== 0 && i !== 14) {
          out[i] = sdk[i];
        }
      }
      if (out.length > 14) out[14] = 0;
      return out;
    }
    // Pro：仅同步 Esc、F1–F12，末两格为小屏+旋钮占位
    for (let i = 0; i < 13 && i < sdk.length; i++) {
      const c = sdk[i];
      if (c === 41 || (c >= 58 && c <= 69)) out[i] = c;
    }
    if (out.length > 13) out[13] = 0;
    if (out.length > 14) out[14] = 0;
    return out;
  }

  const sdkKeys = sdk.filter(
    (c) => c !== 0 && !DK75_STRIP_FROW_KEYCODES.has(c) && c !== 228
  );
  let si = 0;
  for (let ri = 0; ri < out.length; ri++) {
    if (out[ri] === 0) {
      if (rowIndex === 3 && ri === 12) continue;
      /** Shift 行 LShift 后 col1 占位列（与 Gamma75 / 固件矩阵一致） */
      if (rowIndex === 4 && ri === 1) continue;
      if (rowIndex === 5) continue;
      continue;
    }
    if (si < sdkKeys.length) out[ri] = sdkKeys[si++];
  }
  return out;
}

/** DK75 Pro / ZE75：F 行末小屏+旋钮占位，SDK 回读时去掉 PrtScr 系 */
export function normalizeDk75ProSdkKeycodeRow(
  rowIndex: number,
  sdkRow: number[]
): number[] {
  return normalizeDk75FamilySdkKeycodeRow(rowIndex, sdkRow, "dk75Pro");
}

/** DK75 标准款：F 行 PrtScr 实键 + 旋钮占位 */
export function normalizeDk75SdkKeycodeRow(
  rowIndex: number,
  sdkRow: number[]
): number[] {
  return normalizeDk75FamilySdkKeycodeRow(rowIndex, sdkRow, "dk75");
}

/** DK75 E：F 行按 SDK 矩阵列回读，含 PrtScr / Insert 改键后的 keycode */
export function normalizeDk75ESdkKeycodeRow(
  rowIndex: number,
  sdkRow: number[]
): number[] {
  return normalizeDk75FamilySdkKeycodeRow(rowIndex, sdkRow, "dk75E");
}

/** @deprecated 请使用 normalizeDk75ESdkKeycodeRow */
export function normalizeDk75eSdkKeycodeRow(
  rowIndex: number,
  sdkRow: number[]
): number[] {
  return normalizeDk75ESdkKeycodeRow(rowIndex, sdkRow);
}

/**
 * 批量转换多行布局
 */
export const convertLayoutRows = (
  rows: SDKRowLayout[],
  widthsPerRow: number[][],
  keyboardType: KeyboardType = "87",
  layer: number = 0,
  system: number = 0
): KeyConfig[][] => {
  let layoutRows = rows;
  // FIX：feature/dsf/Gamma75-fix-fnLayout 修复偏移
  // if (keyboardType === "gamma75" && layer !== 0) {
  //   layoutRows = applyGamma75FnLayerToSdkRows(rows);
  // }
  if (shouldApplyHuafendaX87V2FnLayer(keyboardType, layer)) {
    layoutRows = applyHuafendaX87V2FnLayerToSdkRows(layoutRows);
  }
  if (shouldApplyHuafendaM98ScreenFnLayer(keyboardType, layer)) {
    layoutRows = applyHuafendaM98ScreenFnLayerToSdkRows(layoutRows);
  }
  if (shouldApplyHuafendaSmart875FnLayer(keyboardType, layer)) {
    layoutRows = applyHuafendaSmart875FnLayerToSdkRows(layoutRows);
  }
  if (shouldApplyHuafendaX98_1KFnLayer(keyboardType, layer)) {
    layoutRows = applyHuafendaX98_1KFnLayerToSdkRows(layoutRows);
  }
  layoutRows = applyX87MacBaseLayerToSdkRows(
    layoutRows,
    keyboardType,
    layer,
    system
  );
  const keyConfigRows = layoutRows.map((row, rowIndex) => {
    let keyCodes = row.keyboardLayout;
    // if (keyboardType === "deep80max" || keyboardType === "84") {
    //   keyCodes = sanitizeDeep80MaxLayoutKeycodeRow(
    //     row.row ?? rowIndex,
    //     keyCodes ?? []
    //   );
    // }
    let rowWidths = widthsPerRow[rowIndex];
    let rowFirmwareCols: number[] | undefined;
    if (keyboardType === "dk75Pro") {
      keyCodes = normalizeDk75ProSdkKeycodeRow(rowIndex, keyCodes ?? []);
    }
    if (keyboardType === "dk75") {
      keyCodes = normalizeDk75SdkKeycodeRow(rowIndex, keyCodes ?? []);
    }
    if (keyboardType === "dk75E") {
      keyCodes = normalizeDk75ESdkKeycodeRow(rowIndex, keyCodes ?? []);
    }
    if (isX75v3KeyboardType(keyboardType)) {
      keyCodes = normalizeGamma75ShiftRowKeyCodes(
        trimKeyCodeRowTrailingZeros(keyCodes ?? []),
        rowIndex,
        keyboardType
      );
      ({
        keyCodes,
        widths: rowWidths,
        firmwareCols: rowFirmwareCols,
      } = compactX75v3MatrixRow(keyCodes, rowWidths, rowIndex, keyboardType));
    }
    if (keyboardType === "dk63Machine") {
      keyCodes = normalizeDk63MachineSdkKeycodeRow(rowIndex, keyCodes ?? []);
      rowWidths = getDk63MachineRowWidths(rowIndex, rowWidths);
    }
    return convertRowLayoutToKeyConfigs(
      keyCodes,
      rowWidths,
      rowIndex,
      keyboardType,
      rowFirmwareCols,
      layer,
      system
    );
  });
  return applyX87MacBaseLayerToKeyConfigs(
    keyConfigRows,
    keyboardType,
    layer,
    system
  );
};

/**
 * ✅ 生成默认布局（用于无 SDK 连接时展示）
 */
/** 裁掉矩阵行尾用于补齐列数的 0，避免虚拟键盘右侧被撑宽 */
const trimKeyCodeRowTrailingZeros = (row: number[]): number[] => {
  let last = row.length - 1;
  while (last >= 0 && row[last] === 0) {
    last -= 1;
  }
  return row.slice(0, last + 1);
};

const isLayoutGapKey = (key: KeyConfig): boolean =>
  key.code === "__gap__" || (key.keyCode === 1 && key.multipleWidth === 0);

/** x75v3 / gamma75：压紧后 col 还原为静态矩阵列号，供高级键 SDK 读写 */
const resolveX75v3FirmwareMatrixCol = (
  keyboardType: KeyboardType,
  rowIndex: number,
  keyCode: number,
  fallbackCol: number
): number => {
  const matrix = getKeyCodeMatrix(keyboardType)[rowIndex] ?? [];
  const trimmed = trimKeyCodeRowTrailingZeros(matrix);
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === keyCode) return i;
  }
  return fallbackCol;
};

/**
 * x75v3 / gamma75：按静态矩阵压紧后的键序取宽度，不跟固件/SDK 占位列下标绑定。
 */
export const getX75v3StaticCompactRowWidths = (
  rowIndex: number,
  keyboardType: KeyboardType = "x75v3"
): number[] => {
  const matrix = getKeyCodeMatrix(keyboardType);
  const widths = getKeyWidthsByType(keyboardType)[rowIndex] ?? [];
  const trimmed = trimKeyCodeRowTrailingZeros(matrix[rowIndex] ?? []);
  const result: number[] = [];
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] !== 0) {
      result.push(widths[i] ?? 1);
    }
  }
  return result;
};

/** 将 layout 中每颗实键的 multipleWidth 同步为静态宽度表 */
export const applyX75v3StaticWidthsToLayout = (
  layout: KeyConfig[][],
  keyboardType: KeyboardType = "x75v3"
): KeyConfig[][] => {
  const staticTable = getKeyWidthsByType(keyboardType);
  return layout.map((row, rowIndex) => {
    const rowTable = staticTable[rowIndex] ?? [];
    const compactWidths = getX75v3StaticCompactRowWidths(
      rowIndex,
      keyboardType
    );
    let compactIndex = 0;
    return row.map((key) => {
      if (isLayoutGapKey(key)) return key;
      // 第 6 行空格两侧有 matrix 占位 0，须按 originalCol 取宽，不能按「第几颗实键」累加
      if (
        rowIndex === 5 &&
        key.originalCol !== undefined &&
        key.originalCol >= 0
      ) {
        const spaceBarWidth = rowTable[6];
        const splitSpaceKeys = row.filter(
          (k) => !isLayoutGapKey(k) && k.keyCode === 44
        );
        if (
          key.keyCode === 44 &&
          splitSpaceKeys.length > 1 &&
          spaceBarWidth > 0
        ) {
          const splitTotalWidth =
            keyboardType === "dk75" ||
            keyboardType === "dk75Pro" ||
            keyboardType === "dk75E"
              ? Math.max(
                  0,
                  Number(
                    (
                      spaceBarWidth + DK75_LIGHTING_SPACE_SPLIT_WIDTH_ADJUST
                    ).toFixed(3)
                  )
                )
              : spaceBarWidth;
          return {
            ...key,
            multipleWidth: Number(
              (splitTotalWidth / splitSpaceKeys.length).toFixed(3)
            ),
          };
        }
        const matrixWidth = rowTable[key.originalCol];
        if (matrixWidth !== undefined && matrixWidth > 0) {
          return { ...key, multipleWidth: matrixWidth };
        }
      }
      const w = compactWidths[compactIndex];
      compactIndex += 1;
      return w !== undefined ? { ...key, multipleWidth: w } : key;
    });
  });
};

/**
 * x75v3：固件/SDK 矩阵中行内 0 占位列若保留，会把 Backspace/Enter 等的 multipleWidth
 * 渲染成空白间隙（红框处），真实按键反而被压成 1u。第 6 行空格两侧占位 0 需保留。
 * 宽度一律用 getX75v3StaticCompactRowWidths，不跟占位列下标走。
 */
export const compactX75v3MatrixRow = (
  keyCodes: number[],
  _widths: number[],
  rowIndex: number,
  keyboardType: KeyboardType = "x75v3"
): { keyCodes: number[]; widths: number[]; firmwareCols: number[] } => {
  if (rowIndex === 5) {
    const refCodes = trimKeyCodeRowTrailingZeros(
      getKeyCodeMatrix(keyboardType)[5] ?? []
    );
    const refWidths = (getKeyWidthsByType(keyboardType)[5] ?? _widths).slice(
      0,
      refCodes.length
    );
    const trimmed = trimKeyCodeRowTrailingZeros(keyCodes);
    const sdkKeys = trimmed.filter((c) => c !== 0);
    const refKeys = refCodes.filter((c) => c !== 0);
    if (
      sdkKeys.length === refKeys.length &&
      sdkKeys.every((code, i) => code === refKeys[i])
    ) {
      return {
        keyCodes: refCodes,
        widths: refWidths,
        firmwareCols: refCodes.map((_, i) => i),
      };
    }
    return {
      keyCodes: trimmed,
      widths: refWidths.slice(0, trimmed.length),
      firmwareCols: trimmed.map((_, i) => i),
    };
  }
  const trimmed = trimKeyCodeRowTrailingZeros(keyCodes);
  const compactCodes: number[] = [];
  const firmwareCols: number[] = [];
  const refMatrix = getKeyCodeMatrix(keyboardType);
  for (let i = 0; i < trimmed.length; i++) {
    const refSlot = refMatrix[rowIndex]?.[i];
    /** Gamma75 Fn 空键(0) 占住 Base 实键位，须保留列以免 F 行错位 */
    const keepGamma75FnEmptySlot =
      keyboardType === "gamma75" &&
      trimmed[i] === 0 &&
      typeof refSlot === "number" &&
      refSlot !== 0;
    if (trimmed[i] !== 0 || keepGamma75FnEmptySlot) {
      compactCodes.push(trimmed[i]);
      /** 压紧后仍保留 SDK 原始列号，供校准 ADC / 高级键读写对齐 */
      firmwareCols.push(i);
    }
  }
  const staticWidths = getX75v3StaticCompactRowWidths(rowIndex, keyboardType);
  const compactWidths = compactCodes.map((_, i) => staticWidths[i] ?? 1);
  return { keyCodes: compactCodes, widths: compactWidths, firmwareCols };
};

/**
 * 贝盈子应用 / SDK getKeyLayout 以 `KEYBOARD_LAYOUTS` 为矩阵源。
 * 神光同步预览勿走 `getKeyCodeMatrix`（如 m75 误映射 x75v2）。
 */
export const generateLayoutFromKeyCodeMapperMatrix = (
  keyboardType: KeyboardType
): KeyConfig[][] | null => {
  const matrix =
    KEYBOARD_LAYOUTS[keyboardType as keyof typeof KEYBOARD_LAYOUTS];
  if (!matrix?.length) return null;
  const rows = matrix.map((row, rowIndex) => ({
    layer: 0,
    row: rowIndex,
    keyboardLayout: row.map((cell) => (cell === 0 ? 0 : cell)),
  }));
  return convertLayoutRows(
    rows,
    getKeyWidthsByType(keyboardType),
    keyboardType,
    0
  );
};

export const generateDefaultLayout = (
  keyboardType: KeyboardType,
  system: number = 0
): KeyConfig[][] => {
  const keyCodes = getKeyCodeMatrix(keyboardType);
  const widths = getKeyWidthsByType(keyboardType);

  const layout = keyCodes.map((row, rowIndex) => {
    let rowKeyCodes = row;
    let rowWidths = widths[rowIndex];
    let rowFirmwareCols: number[] | undefined;
    if (isX75v3KeyboardType(keyboardType)) {
      const normalizedRow = normalizeGamma75ShiftRowKeyCodes(
        trimKeyCodeRowTrailingZeros(row),
        rowIndex,
        keyboardType
      );
      ({
        keyCodes: rowKeyCodes,
        widths: rowWidths,
        firmwareCols: rowFirmwareCols,
      } = compactX75v3MatrixRow(
        normalizedRow,
        widths[rowIndex],
        rowIndex,
        keyboardType
      ));
    }
    if (keyboardType === "dk63Machine") {
      rowKeyCodes = normalizeDk63MachineSdkKeycodeRow(rowIndex, rowKeyCodes);
      rowWidths = getDk63MachineRowWidths(rowIndex, rowWidths);
    }
    return convertRowLayoutToKeyConfigs(
      rowKeyCodes,
      rowWidths,
      rowIndex,
      keyboardType,
      rowFirmwareCols,
      0,
      system
    );
  });
  return applyX87MacBaseLayerToKeyConfigs(layout, keyboardType, 0, system);
};
/**
 * 默认 65% 键盘的按键宽度配置（6 行）
 */
export const TOTAL_63_KEY_WIDTHS: number[][] = [
  //Row 0 占位，避免报错
  [0],
  // Row 1: 1 + 1×12 + 2 = 14 keys
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
  // Row 2: 1.5 + 1×12 + 1.5 = 14 keys
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
  // Row 3: 1.75 + 1×11 + 2.32 + 空×1 = 14 keys (实际 15 列，Col 13 是空键)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.32],
  // Row 4: …Z–M、,、.；公版 col11–13=/?(1u)、↑、R-Shift(1.9u)。
  // DK63 V2 / 华奋达 DK63Star 的 /?↔R-Shift 键宽由 applyDk63V2SlashRShiftKeyWidths 覆盖。
  [2.15, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9],
  // Row 5: 1.25×3 + 空×3 + 6.25 + 空×2 + 1×5 = 10 实际按键 (10列)
  [1.25, 1.25, 1.25, 0, 0, 0, 6.62, 0, 0, 1, 1, 1, 1, 1],
];

/** SDCX DK63 Machine：与公版 63 键紧凑布局一致（Enter/Shift 行无多余占位列） */
export const TOTAL_DK63_MACHINE_KEY_WIDTHS: number[][] = [
  [0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.32],
  [2.15, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9],
  /** Space 在 col3（与 LAYOUT_63 一致），非 68 系 col6 */
  [1.25, 1.25, 1.25, 6.62, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1],
];

/** DK63 Machine 底行：去掉中间占位列，压紧为 9 颗物理键 */
const DK63_MACHINE_BOTTOM_ROW_WIDTHS = [1.25, 1.25, 1.25, 6.62, 1, 1, 1, 1, 1];

function normalizeDk63MachineBottomRow(keyCodes: number[]): number[] {
  const src = keyCodes ?? [];
  return [
    src[0] ?? 0,
    src[1] ?? 0,
    src[2] ?? 0,
    src[3] ?? 0,
    src[9] ?? 0,
    src[10] ?? 0,
    src[11] ?? 0,
    src[12] ?? 0,
    src[13] ?? 0,
  ];
}

/** DK63 Machine：SDK 行数据归一（底行压紧为 9 颗物理键） */
export function normalizeDk63MachineSdkKeycodeRow(
  rowIndex: number,
  keyCodes: number[]
): number[] {
  if (rowIndex === 5) {
    return normalizeDk63MachineBottomRow(keyCodes);
  }
  return keyCodes ?? [];
}

function getDk63MachineRowWidths(
  rowIndex: number,
  defaultWidths: number[]
): number[] {
  if (rowIndex === 5) {
    return DK63_MACHINE_BOTTOM_ROW_WIDTHS;
  }
  return defaultWidths;
}

/**
 * 默认 65% 键盘的按键宽度配置（6 行）
 */
export const TOTAL_68_KEY_WIDTHS: number[][] = [
  // Row 0: 全空 21 列
  [0],
  // Row 1: Esc + 1-0 + -= + Bksp + Home + 6×0 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 0, 0, 0, 0, 0, 0],
  // Row 2: Tab + Q-P + []\ + End + 6×0 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5, 1, 0, 0, 0, 0, 0, 0],
  // Row 3: Caps + A-L;' + gap + Enter + PgUp + 6×0 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.3, 1, 0, 0, 0, 0, 0, 0],
  // Row 4: Shift + gap + Z-M,./ + Shift + ↑ + PgDn + 6×0 (21 cols)
  [2.15, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9, 1, 0, 0, 0, 0, 0, 0],
  // Row 5: Ctrl Win Alt + gap×3 + Space + gap×2 + Alt Fn Ctrl + ←↓→ + 6×0 (21 cols)
  [1.25, 1.25, 1.25, 0, 0, 0, 6.62, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
];

/** BAT68：底行移除右 Ctrl，右 Alt/Fn 均为 1.25U，Fn 与左箭头间留 0.5U */
export const TOTAL_HUAFENDA68_KEY_WIDTHS: number[][] = [
  ...TOTAL_68_KEY_WIDTHS.slice(0, 5),
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.62, 0, 0, 1.25, 1.25, 0.5, 1, 1, 1, 0, 0, 0, 0,
    0, 0,
  ],
];

/** Tank68 HE：Row 0–4 同 68；Row 5 为标准 68 底行（含 R-Ctrl 1u） */
export const TOTAL_TANK68HE_KEY_WIDTHS: number[][] = TOTAL_68_KEY_WIDTHS;

/**
 * 默认 65% 键盘的按键宽度配置（6 行）
 */
export const TOTAL_68EVO_KEY_WIDTHS: number[][] = [
  //Row 0 占位，避免报错
  [0],
  // Row 1: 1 + 1×12 + 2 + 1 = 14 keys
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
  // Row 2: 1.5 + 1×12 + 1.5 + 1 = 14 keys
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
  // Row 3: 1.75 + 1×11 + 2.25 + 1 = 14 keys (实际 15 列，Col 13 是空键)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.32],
  // Row 4: 2.25 + 1×10 + 1.75 + 1 + 1 = 14 keys (实际 15 列，Col 13 是空键)
  [2.15, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9, 1],
  // Row 5: 1.25×3 + 空×3 + 6.25 + 空×3 +  1×3 = 10 实际按键 (10列)
  [1.25, 1.25, 1.25, 0, 2.2, 0, 2.05, 0, 2.2, 1, 1, 1, 1, 1, 1],
];

/**
 * 80% TKL 键盘的按键宽度配置（6 行 × 21 列）
 */
export const TOTAL_87_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  // Row 2: Tab 行 + 导航键 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  // Row 3: Caps 行 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.47, 0, 0, 0, 0, 0, 0, 0],
  // Row 4: Shift 行 + 方向上 (21 cols)
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 3.05, 0, 0, 1, 0, 0, 0, 0, 0],
  // Row 5: 控制行 + 方向键 (21 cols)
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.35, 1.39, 1, 1, 1,
    0, 0, 0, 0,
  ],
];

export const TOTAL_ES87_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.47, 0, 0, 0, 0, 0, 0, 0],
  // 与 LAYOUT_87 同构：L-Shift 后占位列 + 10×1u（/?）+ R-Shift 2.97（与 Enter 右缘对齐）
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.97, 0, 0, 1, 0, 0, 0, 0, 0],
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.35, 1.39, 1, 1, 1,
    0, 0, 0, 0,
  ],
];

export const TOTAL_HUAFENDA87_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  // Row 2: Tab 行 + 导航键 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  // Row 3: Caps 行 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.47, 0, 0, 0, 0, 0, 0, 0, 0],
  // Row 4: Shift 行 + 方向上 (21 cols)
  [2.55, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.75, 0, 0, 1, 0, 0, 0, 0, 0, 0],
  // Row 5: 控制行 + 方向键 (21 cols)
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.35, 1.39, 1, 1, 1,
    0, 0, 0, 0,
  ],
];

export const TOTAL_X75V2_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.42, 1, 0, 0, 0, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.75, 1, 0, 0, 0, 0, 0, 0, 0, 0],
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 0, 1.25, 1.25, 1, 1, 1, 0, 0, 0,
    0, 0,
  ],
];

/** M75 与 X75V2 底行修饰键布局不同，键宽单独维护 */
export const TOTAL_M75_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.42, 1, 0, 0, 0, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.75, 1, 0, 0, 0, 0, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
];

/** X75GC：F 行 PrtSc/Home 相邻；底行无 RCtrl，RAlt/Fn 1.25U（同 BAT68） */
export const TOTAL_X75GC_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.42, 1, 0, 0, 0, 0, 0, 0, 0],
  [2.42, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.75, 1, 0, 0, 0, 0, 0, 0, 0, 0],
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.78, 0, 0, 1.25, 1.25, 0, 1, 1, 1, 0, 0, 0, 0,
    0, 0,
  ],
];

export const TOTAL_X75V3_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + F1–F12 + PrtScr / Scroll / Insert（16×1u，不调整）
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  // Row 1: Backspace 加宽 → Home 与 Insert 纵向对齐
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.075, 1, 0, 0, 0, 0, 0, 0],
  // Row 2: \| 加宽 → Delete 与 Insert 纵向对齐
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.575, 1, 0, 0, 0, 0, 0, 0],
  // Row 3: Enter 加宽 → PgUp 与 Insert 纵向对齐
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.4, 1, 0, 0, 0, 0, 0, 0, 0],
  // Row 4: /? 1u；R-Shift 加宽 → ↑ 与 ↓ 对齐、PgDn 与 Insert 对齐
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.895, 1, 1, 0, 0, 0, 0, 0, 0, 0],
  // Row 5: LCtrl/LWin/LAlt(1.25) + Space(6.45) + RAlt/Fn/RCtrl(1) + ←↓→(1)；↓ 对齐 ↑、→ 对齐 PgDn
  [1.25, 1.25, 1.25, 0, 0, 0, 6.45, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
];

export const TOTAL_GAMMA75_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.075, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.575, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.4, 1, 0, 0, 0, 0, 0, 0, 0],
  /** LShift 后 col1 占位列（与 ES68 / LAYOUT_GAMMA75 一致） */
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.895, 1, 1, 0, 0, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.45, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
];

/** DK75 灯光页：Space 拆 3 段时多 2 个 keyGap，拆分总宽略减以对齐改键布局 */
export const DK75_LIGHTING_SPACE_SPLIT_WIDTH_ADJUST = -0.15;

/** DK75 / ZE75：F 行 15 键；Enter/空格宽度与 x75v3 一致；底行 Space@6 三灯珠 */
export const TOTAL_DK75_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.075, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.575, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.4, 1, 0, 0, 0, 0, 0, 0],
  /** LShift 略缩；col1 占位列对齐固件/Gamma75，高级键与校准列号正确 */
  [2.1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.895, 1, 1, 0, 0, 0, 0, 0, 0, 0],
  /** 底行无 RCtrl：加宽 LCtrl/LWin/LAlt/Space/RAlt，使 ↓ 对齐 ↑、→ 对齐 PgDn */
  [1.5, 1.3, 1.3, 0, 0, 0, 6.915, 0, 0, 0, 1.3, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
];

export const TOTAL_75PRO_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.42, 0, 0, 0, 0, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.75, 1, 0, 0, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
];

export const TOTAL_M98_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.8, 1, 1, 1, 1, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.3, 1, 1, 1, 1, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.12, 1, 1, 1, 0, 0, 0, 0],
  // ↑ 1 2 3 Enter：宽度用 ||1 兜底；Enter 与 0 同列由竖跨例外处理
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.68, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
];

export const TOTAL_M98TFT_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.8, 1, 1, 1, 1, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.3, 1, 1, 1, 1, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.12, 1, 1, 1, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.68, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
];

export const TOTAL_M98_8K_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.8, 1, 1, 1, 1, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.3, 1, 1, 1, 1, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.12, 1, 1, 1, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.68, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
];

export const TOTAL_75PROTFT_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 0, 0, 0, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.42, 0, 0, 0, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.75, 1, 1, 0, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
];
/**
 * 84% TKL 键盘的按键宽度配置（6 行 × 按物理键盘排列）
 */
/** DEEP80 MAX：与 84 键宽一致（Shift 行 2.25 / 2.97 与 Enter 纵向对齐） */
export const TOTAL_DEEP80MAX_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.47, 0, 0, 0, 0, 0, 0, 0],
  // Row 4: L-Shift 右缘 ~A 中心(2.25u)；R-Shift 左缘 ~' 中心(12.25u)、右缘与 Enter 右缘对齐(2.97u)
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 3.07, 0, 0, 1, 0, 0, 0, 0, 0],
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.74, 1, 1, 1, 1, 0,
    0, 0, 0,
  ],
];

export const TOTAL_84_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  // Row 2: Tab 行 + 导航键 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  // Row 3: Caps 行 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.47, 0, 0, 0, 0, 0, 0, 0],
  // Row 4: L-Shift 右缘 ~A 中心(2.25u)；R-Shift 左缘 ~' 中心(12.25u)、右缘与 Enter 右缘对齐(2.97u)
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.97, 0, 0, 1, 0, 0, 0, 0, 0],
  // Row 5: 控制行 + 方向键 (21 cols)
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.74, 1, 1, 1, 1, 0,
    0, 0, 0,
  ],
];

/**
 * 84% TKL 键盘的按键宽度配置（6 行 × 按物理键盘排列）
 */
export const TOTAL_99_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1, 0, 0, 0, 0],
  // Row 2: Tab 行 + 导航键 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1, 0, 0, 0, 0],
  // Row 3: Caps 行 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.47, 1, 1, 1, 1, 1, 0, 0, 0],
  // Row 4: Shift 行 + 方向上 (21 cols)
  [2.1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 0, 0, 1, 1, 1, 1, 0, 0],
  // Row 5: 控制行 + 方向键 (21 cols)
  [
    1.36, 1.36, 1.36, 0, 0, 0, 5.5, 0, 0, 0, 1.35, 1.35, 1.35, 1, 1, 1, 1, 0, 0,
    0, 0,
  ],
];

/**
 * 84% TKL 键盘的按键宽度配置（6 行 × 按物理键盘排列）
 */
/** M80 / M71 机甲键盘（与 KEYBOARD_LAYOUTS.m80 一致，19 列） */
export const TOTAL_M80_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.45, 1, 0, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.95, 1, 1, 1, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.05, 0, 0, 0, 1.35, 1.35, 1, 1, 1, 1, 0, 0, 0],
];

/** M71 默认与 M80 同宽 */
export const TOTAL_M71_KEY_WIDTHS: number[][] = [
  [0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 0, 0, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 0, 0, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.45, 1, 0, 0, 0, 0, 0],
  [2.6, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.7, 1, 1, 1, 0, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.05, 0, 0, 0, 1.35, 1.35, 1, 1, 1, 1, 0, 0, 0],
];

/** T87（6×17，键宽对齐 huafenda87 / X87 紧凑列） */
export const TOTAL_T87_KEY_WIDTHS: number[][] = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.47, 0, 0, 0, 0],
  [2.55, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.75, 0, 1, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.25, 1.35, 1.35, 1.39, 1, 1, 1],
];

export const TOTAL_98_KEY_WIDTHS: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 1, 1, 1, 1, 0, 0],
  // Row 2: Tab 行 + 导航键 (21 cols)
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 1, 1, 1, 1, 0, 0],
  // Row 3: Caps 行 (21 cols)
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.45, 1, 1, 1, 1, 1, 0, 0, 0],
  // Row 4: Shift 行 + 方向上 (21 cols)
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.95, 1, 0, 1, 1, 1, 1, 0, 0],
  // Row 5: 控制行 + 方向键 (21 cols)
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.52, 0, 0, 0, 1.35, 1.35, 1, 1, 1, 2.05, 0, 1,
    0, 0, 0, 0,
  ],
];

/**
 * V102 宽度（对齐基准，对齐全尺寸 98 逻辑）
 * - BS / \ / Enter 右缘 = 15.3
 * - Ins / PgUp / PgDn / Del 左缘 = 15.6
 * - R-Shift 右缘 = 14.6，右侧由 ↑ 接到 15.6（避免主区右缘空洞）
 */
export const TOTAL_V102_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.2, 1.2, 1.2, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.1, 1, 1, 1, 1, 1, 0, 0],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.6, 1, 1, 1, 1, 1, 0, 0],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.4, 1, 1, 1, 1, 0, 0, 0, 0],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.35, 1, 0, 1, 1, 1, 1, 0, 0, 0],
  [
    1.25, 1.25, 1.25, 0, 0, 0, 6.55, 0, 0, 0, 1, 1, 1, 1, 1, 1, 3.15, 1, 0, 0,
    0,
  ],
];

// 创建 100% 键盘布局的函数
export const createFullKeyboardKeyWidth = (): number[][] => {
  // 用于替换后四位的新 KeyCode 数组，您可以根据实际需求进行调整
  const replacements: number[][] = [
    [0, 0, 0, 0], // 第一行替换
    [0, 0, 0, 0], // 第二行替换
    [0, 0, 0, 0], // 第三行替换
    [0, 0, 0, 0], // 第四行替换
    [0, 0, 0, 0], // 第五行替换
    [2.1, 0, 0, 0], // 第六行替换
  ];

  return TOTAL_87_KEY_WIDTHS.map((row, rowIndex) => {
    // 根据行索引返回替换后的行
    return rowIndex < replacements.length
      ? [...row.slice(0, 17), ...replacements[rowIndex]] // 保留前 17 个元素并替换后四位
      : row; // 默认返回原行，通常不会到这里
  });
};

export const TOTAL_104_KEY_WIDTHS = createFullKeyboardKeyWidth();

/** DK108：108 键全尺寸键宽（独立维护，初版与 104 一致） */
export const TOTAL_DK108_KEY_WIDTHS = createFullKeyboardKeyWidth();

/**
 * 贝盈 web-hid 改键成功后局部更新虚拟键，避免 2.4G 每次 setKeyCode 全量 getKeymaps
 */
export function patchLayoutKeyAtPosition(
  layout: KeyConfig[][],
  row: number,
  col: number,
  keyCode: number,
  keyboardType: KeyboardType = "87"
): KeyConfig[][] {
  return layout.map((layoutRow, ri) =>
    layoutRow.map((cell) => {
      if (cell.code === "__gap__") return cell;
      const cellRow = cell.originalRow ?? ri;
      if (cellRow !== row || cell.originalCol !== col) return cell;

      const keyInfoRaw = resolveKeyCodeInfoForVirtualKey(keyCode, keyboardType);
      const keyInfo = applyM71VirtualKeyLegendOverride(
        keyboardType,
        row,
        col,
        keyCode,
        keyInfoRaw
      );
      const rongyuanPhysicalInfo = resolveRongyuanAdvancedSlotKeyInfo(
        keyboardType,
        row,
        col,
        keyCode
      );
      const webKeySource =
        keyboardType === "m80" ||
        keyboardType === "m71" ||
        keyboardType === "t87" ||
        keyboardType === "x75v2" ||
        keyboardType === "x75gc" ||
        keyboardType === "m75" ||
        keyboardType === "X98_1K"
          ? (MECHA_FIRMWARE_TO_LAYOUT_HID[keyCode] ?? keyCode)
          : keyCode;
      const webKey = KEYCODE_TO_WEB_KEY_MAP[webKeySource] || `KEY_${keyCode}`;

      if (!keyInfo && !rongyuanPhysicalInfo) {
        return {
          ...cell,
          keyCode,
          code: keyCode === 0 ? "__gap__" : webKey,
          label: keyCode === 0 ? "" : String(keyCode),
        };
      }

      const resolvedInfo = rongyuanPhysicalInfo ?? keyInfo!;

      return {
        ...cell,
        keyCode,
        code: webKey,
        label: resolvedInfo.name,
        comm: resolvedInfo.comm ?? cell.comm,
        uuid: rongyuanPhysicalInfo
          ? cell.uuid
          : `${resolvedInfo.name}-${keyCode}-${resolvedInfo.hex16}-${cellRow}-${col}`,
      };
    })
  );
}
